import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {
  selectPagination,
  setCursorPagination,
  PAGINATION,
  EMPTY_PAGINATION_OBJECT
} from 'ducks/pagination';
import {composeActions} from 'ducks/common';
import {storeCursorPaginationInUrl} from 'ducks/urls';
import {getCursorPaginationFromUrl} from 'utils/pagination';
import {ExtraAction} from 'ducks/types';

const defaultOptions = {
  saveToUrl: false,
  pageSizes: [10, 25, 50],
  pageSize: 10
};

export interface WithCursorPaginationProps {
  getPagination: () => {
    pageSize: number;
    pageSizes: number[];
    cursor: number;
    hasPrevious: boolean;
    hasNext: boolean;
  };
  onPaginationChange: (
    {pageSize, cursor}: {pageSize: number; cursor?: number},
    extraActions?: ExtraAction[]
  ) => any;
}

const withCursorPagination = <T extends any>(
  Component: React.ComponentType<T & WithCursorPaginationProps>,
  namespace: string,
  options: Partial<typeof defaultOptions> = {}
) => {
  const combinedOptions = {...defaultOptions, ...options};

  class WrappedComponent extends React.Component<any> {
    state = {
      pagionationHasMounted: false
    };

    componentDidMount() {
      const paginationFromUrl = getCursorPaginationFromUrl();

      if (combinedOptions.saveToUrl && !_.isEmpty(paginationFromUrl)) {
        this.props.setCursorPagination({
          ...paginationFromUrl,
          namespace
        });
      } else {
        // Sets initial pagination to EMPTY
        this.props.setCursorPagination({
          ...EMPTY_PAGINATION_OBJECT,
          pageSize: combinedOptions.pageSize,
          namespace
        });
      }

      this.setState({pagionationHasMounted: true});
    }

    componentWillUnmount() {
      if (combinedOptions.saveToUrl) {
        this.props.storeCursorPaginationInUrl({
          paginationSelector: () => ({
            pageSize: null,
            cursor: null,
            hasPrevious: null,
            hasNext: null
          })
        });
      }
    }

    getPagination = () => ({
      ...this.props.paginationInStore,
      pageSizes: combinedOptions.pageSizes
    });

    onPaginationChange = (
      {
        pageSize,
        cursor
      }: {
        pageSize: number;
        cursor?: number;
      },
      extraActions = []
    ) => {
      const actions: Array<() => any> = [
        () => setCursorPagination({pageSize, namespace, cursor})
      ];

      if (combinedOptions.saveToUrl) {
        actions.push(() =>
          storeCursorPaginationInUrl({
            paginationSelector: state => selectPagination(state, namespace)
          })
        );
      }

      return this.props.composeActions([...actions, ...extraActions]);
    };

    render() {
      // This downcast is important to remove the added props of the WrappedComponent
      const restOfProps: T = this.props;

      // This fixes a pagination bug with the HOC getting mounted after the wrapped Component.
      // The wrapped component then uses the default value for pagination from the store because the
      // requested value hasn't been set yet.
      if (!this.state.pagionationHasMounted) return null;

      return (
        <Component
          {...restOfProps}
          getPagination={this.getPagination}
          onPaginationChange={this.onPaginationChange}
        />
      );
    }
  }

  const mapStateToProps = state => ({
    paginationInStore: selectPagination(state, namespace)
  });

  const mapDispatchToProps = {
    composeActions,
    setCursorPagination,
    storeCursorPaginationInUrl
  };

  return connect(mapStateToProps, mapDispatchToProps)(
    WrappedComponent as React.ComponentType<T>
  );
};

export {withCursorPagination, PAGINATION};
